import React, { useState } from 'react';
import { Loader, Modal, Select } from '@mantine/core';
import { formatTenbisDesiredTime, type TenbisClosingTimeFrame } from 'shared-models/browser';
import { Button } from '../../../../components/Button';
import { useTenbisWebsiteModeController } from './useTenbisWebsiteModeController';
import classes from './tenbis-website-mode-modal.module.scss';

type TenbisWebsiteModeModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string | null;
};

const TIME_FRAME_OPTIONS: { value: TenbisClosingTimeFrame; label: string }[] = [
  { value: 'HalfAnHour', label: 'חצי שעה' },
  { value: 'OneHour', label: 'שעה' },
  { value: 'TwoHours', label: 'שעתיים' },
  { value: 'ThreeHours', label: 'שלוש שעות' },
  { value: 'TomorrowMorning', label: 'עד מחר בבוקר' },
];

// Manager temp-close on 10bis: shows the LAST SENT state (no readback) + click-guarded toggle.
export const TenbisWebsiteModeModal: React.FC<TenbisWebsiteModeModalProps> = ({ opened, onClose, tenantId, employeeSession }) => {
  const { state, isLoading, isSaving, error, setWebsiteMode } = useTenbisWebsiteModeController({ tenantId, employeeSession, opened });
  const [timeFrame, setTimeFrame] = useState<TenbisClosingTimeFrame>('TomorrowMorning');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const closed = state?.mode === 'temp_closed';
  const untilLabel = closed ? (state?.until ? `עד ${formatTenbisDesiredTime(state.until)}` : 'עד מחר בבוקר') : null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<span>{'תן ביס: סטטוס המסעדה'}</span>}
      centered
      radius="md"
      zIndex={302}
      closeButtonProps={{ 'data-testid': 'tenbis-website-close' } as Record<string, string>}
    >
      <div className={classes.stack} data-testid="tenbis-website-modal">
        {error && <p className={classes.error} data-testid="tenbis-website-error">{error}</p>}
        {isLoading ? (
          <div className={classes.center}>
            <Loader />
          </div>
        ) : state ? (
          <>
            <div className={classes.statusRow}>
              <span className={classes.label}>{'נשלח לתן ביס'}</span>
              <span className={`${classes.pill} ${closed ? classes.pillOffline : classes.pillOnline}`} data-testid="tenbis-website-status">
                {closed ? 'סגור זמנית' : state.mode === 'normal' ? 'פתוח' : 'לא נשלח עדיין'}
              </span>
            </div>
            {untilLabel && <p className={classes.text} data-testid="tenbis-website-until">{untilLabel}</p>}
            <p className={classes.hint}>{'הסטטוס משקף את מה שנשלח מהקופה; תן ביס פותח מחדש לבד בתום הזמן.'}</p>
            {!closed && (
              <Select
                label="למשך"
                data={TIME_FRAME_OPTIONS}
                value={timeFrame}
                allowDeselect={false}
                onChange={(value) => setTimeFrame((value as TenbisClosingTimeFrame | null) ?? 'TomorrowMorning')}
                data-testid="tenbis-website-time-frame"
              />
            )}
            <Button
              variant={closed ? 'confirm' : 'cancel'}
              fullWidth
              disabled={isSaving}
              onClick={() => setIsConfirmOpen(true)}
              dataTestId="tenbis-website-toggle"
            >
              {closed ? 'פתח את המסעדה בתן ביס' : 'סגור זמנית בתן ביס'}
            </Button>
          </>
        ) : null}
      </div>
      <Modal
        opened={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title={closed ? 'לפתוח את המסעדה בתן ביס?' : 'לסגור זמנית את המסעדה בתן ביס?'}
        centered
        radius="md"
        zIndex={303}
        closeButtonProps={{ 'data-testid': 'tenbis-website-confirm-close' } as Record<string, string>}
      >
        <div className={classes.stack} data-testid="tenbis-website-confirm-modal">
          <p className={classes.text}>
            {closed
              ? 'לקוחות יוכלו להזמין מהמסעדה דרך תן ביס.'
              : `המסעדה תוצג כסגורה בתן ביס ${TIME_FRAME_OPTIONS.find((option) => option.value === timeFrame)?.label ?? ''}.`}
          </p>
          <div className={classes.actions}>
            <Button variant="cancel" fullWidth onClick={() => setIsConfirmOpen(false)} disabled={isSaving} dataTestId="tenbis-website-cancel">
              ביטול
            </Button>
            <Button
              variant="confirm"
              fullWidth
              loading={isSaving}
              onClick={async () => {
                if (await setWebsiteMode(closed ? { mode: 'normal' } : { mode: 'temp_closed', timeFrame })) {
                  setIsConfirmOpen(false);
                }
              }}
              dataTestId="tenbis-website-confirm"
            >
              {closed ? 'פתח' : 'סגור'}
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};
